<template lang="pug">
.send-document-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // セル：送付状態
    template(#cell(isSended)="row")
      span(v-if="row.item.isSended == true") 送付済
      span(v-else) 未送付
    // セル：公開状態
    template(#cell(isPublic)="row")
      PublicSwitch.mx-auto(v-if="row.item.isSended == true" :id="'switch-' + row.item.id" :value="row.item.isPublic" @onSwitch="onChangePublic(row.item)")
    // セル：公開状態テキスト
    template(#cell(isPublicText)="row")
      span.public(v-if="row.item.isPublic") 公開中
      span.not-public(v-else) 非公開
    // セル：送付日時
    template(#cell(sendDatetime)="row")
      span {{ convertSendDatetime(row.item.sendDatetime) }}
    // セル：宛先
    template(#cell(receivers)="row")
      span(:id="row.item.id + '_receivers'") {{ createReceiversLabel(row.item) }}
      b-popover(:target="row.item.id + '_receivers'" triggers="hover" placement="topleft")
        span {{ createReceiversLabel(row.item) }}
    // セル：文書種類
    template(#cell(category)="row")
      span(:id="row.item.id + '_category'") {{ row.item.categoryName }}
      b-popover(:target="row.item.id + '_category'" triggers="hover" placement="topleft")
        span {{ row.item.categoryName }}
    // セル：タイトル
    template(#cell(title)="row")
      span {{ row.item.title }}
    // セル：オプション
    template(#cell(option)="row")
      .d-flex.flex-column.align-items-center
        span {{ createOptionLabel(row.item) }}
        b-spinner.spinner(v-if="isOptionLoading(row.item)" small) 
        span.option-count(v-else) {{ createOptionCountLabel(row.item) }}
    // セル：文書単位
    template(#cell(unit)="row")
      span {{ convertUnitName(row.item.unit) }}
    // セル：登録情報
    template(#cell(operation)="row")
      .control-buttons
        button.btn.edit-btn.mr-2.disable(v-if="row.item.isSended == false" type="button" disabled) 確認
        button.btn.edit-btn.mr-2(v-else @click="onDetail(row.item)" type="button") 確認
        button.btn.edit-btn.mr-2(@click="onEdit(row.item)" type="button") 修正
        button.btn.delete-btn(@click="onDelete(row.item)" type="button") 削除
    // セル：登録情報
    template(#cell(noOperation)="row")
      .control-buttons
        button.btn.edit-btn.mr-2.disable(v-if="row.item.isSended == false" type="button" disabled) 確認
        button.btn.edit-btn.mr-2(v-else @click="onDetail(row.item)" type="button") 確認
        button.btn.edit-btn(@click="onEdit(row.item)" type="button") 修正
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  GroupOtherUserGroupInfo,
  ResidentGroupInfo,
  SendDocumentFamilyReceiver,
  SendDocumentGetModel,
  SendDocumentOtherUserReceiver,
  SendMessageGroupInfo,
  Type,
  Unit
} from "@/commons/services/SendDocuments";

import PublicSwitch from "@/pc/basics/PublicSwitch.vue";
import { GroupType } from "@/commons/services/SendMessageGroups";
/**
 * ### 部屋テーブルコンポーネント
 */
@Component({
  components: {
    PublicSwitch
  }
})
export default class SendDocumentTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<SendDocumentTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<SendDocumentTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<SendDocumentTableItem> {
    return this.value;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: SendDocumentTableItem, filter: FilterSetting): boolean {
    return this.filterCategoryId(row, filter) && this.filterSearchWords(row, filter);
  }

  public filterCategoryId(row: SendDocumentTableItem, filter: FilterSetting): boolean {
    if (filter.categoryId == "") {
      return true;
    }

    if (row.categoryId == Number(filter.categoryId)) {
      return true;
    } else {
      return false;
    }
  }

  public filterSearchWords(row: SendDocumentTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundReceivers: boolean = true;
    let foundTitle: boolean = true;
    const receivers: string = this.createFilterReceiversLabel(row);

    for (const word of words) {
      // receiversが空の場合は未入力なので見つからない扱い
      // 空でない場合はindexOfで探す
      if (!receivers || (receivers && receivers.indexOf(word) == -1)) {
        foundReceivers = false;
      }

      // titleが空の場合は未入力なので見つからない扱い
      // 空でない場合はindexOfで探す
      if (!row.title || (row.title && row.title.indexOf(word) == -1)) {
        foundTitle = false;
      }
    }

    return foundReceivers || foundTitle;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  public convertSendDatetime(sendDatetime: number | null): string {
    if (sendDatetime == null) {
      return "";
    }
    const datetime: string = this.$dateUtil.convertNumberToString(sendDatetime, "mm月dd日 hh:MM");

    return datetime;
  }

  public convertUnitName(unit: Unit): string {
    if (unit == Unit.SAME) {
      return "全員同じ";
    } else if (unit == Unit.SEPARATE) {
      return "利用者ごと";
    } else {
      return "";
    }
  }

  /**
   * ### receiversに格納されている名前を検索用に作成する
   */
  public createFilterReceiversLabel(item: SendDocumentTableItem): string {
    const targets: string[] = [];
    switch (item.type) {
      case Type.ALL_FAMILY: {
        targets.push("家族全員");

        // 実際に送ったユーザーの情報も追加する
        for (const receiver of item.receivers as Array<SendDocumentFamilyReceiver>) {
          targets.push(`${receiver.familyName}（${receiver.room}・${receiver.name}（${receiver.kanaName}））`);
        }
        break;
      }
      case Type.SELECTED_FAMILY: {
        for (const receiver of item.receivers as Array<SendDocumentFamilyReceiver>) {
          targets.push(`${receiver.familyName}（${receiver.room}・${receiver.name}（${receiver.kanaName}））`);
        }
        break;
      }
      case Type.SELECTED_OTHER_USER: {
        for (const receiver of item.receivers as Array<SendDocumentOtherUserReceiver>) {
          targets.push(receiver.memberName);
        }
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        for (const group of item.sendMessageGroupInfos as Array<SendMessageGroupInfo>) {
          targets.push(group.name);
        }

        // 実際に送ったユーザーの情報も追加する
        if (item.sendMessageGroupInfos && 0 < item.sendMessageGroupInfos.length) {
          if (item.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
            for (const receiver of item.receivers as Array<SendDocumentFamilyReceiver>) {
              targets.push(`${receiver.familyName}（${receiver.room}・${receiver.name}（${receiver.kanaName}））`);
            }
          } else if (item.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
            for (const receiver of item.receivers as Array<SendDocumentOtherUserReceiver>) {
              targets.push(receiver.memberName);
            }
          }
        }
        break;
      }
      case Type.RESIDENT_GROUP: {
        for (const group of item.residentGroupInfos as Array<ResidentGroupInfo>) {
          targets.push(group.name);
        }
        for (const receiver of item.receivers as Array<SendDocumentFamilyReceiver>) {
          targets.push(`${receiver.familyName}（${receiver.room}・${receiver.name}（${receiver.kanaName}））`);
        }
        break;
      }
      case Type.OTHER_USER_GROUP: {
        for (const group of item.otherUserGroupInfos as Array<GroupOtherUserGroupInfo>) {
          targets.push(group.name);
        }
        for (const receiver of item.receivers as Array<SendDocumentOtherUserReceiver>) {
          targets.push(receiver.memberName);
        }
        break;
      }
    }
    return targets.join("、");
  }

  /**
   * ### receiversに格納されている名前を検索用に作成する
   */
  public createReceiversLabel(item: SendDocumentTableItem): string {
    const targets: string[] = [];
    switch (item.type) {
      case Type.ALL_FAMILY: {
        targets.push("家族全員");

        // 実際に送ったユーザーの情報も追加する
        for (const receiver of item.receivers as Array<SendDocumentFamilyReceiver>) {
          targets.push(`${receiver.familyName}（${receiver.room}・${receiver.name}）`);
        }
        break;
      }
      case Type.SELECTED_FAMILY: {
        for (const receiver of item.receivers as Array<SendDocumentFamilyReceiver>) {
          targets.push(`${receiver.familyName}（${receiver.room}・${receiver.name}）`);
        }
        break;
      }
      case Type.SELECTED_OTHER_USER: {
        for (const receiver of item.receivers as Array<SendDocumentOtherUserReceiver>) {
          targets.push(receiver.memberName);
        }
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        for (const group of item.sendMessageGroupInfos as Array<SendMessageGroupInfo>) {
          targets.push(group.name);
        }
        break;
      }
      case Type.RESIDENT_GROUP: {
        for (const group of item.residentGroupInfos as Array<ResidentGroupInfo>) {
          targets.push(group.name);
        }
        break;
      }
      case Type.OTHER_USER_GROUP: {
        for (const group of item.otherUserGroupInfos as Array<GroupOtherUserGroupInfo>) {
          targets.push(group.name);
        }
        break;
      }
    }
    return targets.join("、");
  }

  public createOptionLabel(item: SendDocumentTableItem): string {
    if (item.needConfirm == true) {
      return "確認取得";
    } else if (item.needAgreement == true) {
      return "同意取得";
    } else {
      return "";
    }
  }

  public isOptionLoading(item: SendDocumentTableItem): boolean {
    if (item.isSended == false) {
      return false;
    } else if (item.needConfirm == false && item.needAgreement == false) {
      return false;
    } else if (item.replyedCount != null) {
      return false;
    } else {
      return true;
    }
  }

  public createOptionCountLabel(item: SendDocumentTableItem): string | "" {
    if (item.isSended == false) {
      return "";
    } else if (item.needConfirm == false && item.needAgreement == false) {
      return "";
    }

    return `${item.replyedCount}／${item.receivers.length}人`;
  }

  /**
   * ### 公開/非公開のスイッチ押下イベント
   */
  public onChangePublic(item: SendDocumentTableItem): void {
    this.$emit("onChangePublic", item);
    return;
  }

  public onDetail(item: SendDocumentTableItem): void {
    this.$emit("onDetail", item);
    return;
  }

  /**
   * ### 修正・確認ボタン押下イベント
   */
  public onEdit(item: SendDocumentTableItem): void {
    this.$emit("onEdit", item);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: SendDocumentTableItem): void {
    this.$emit("onDelete", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface SendDocumentTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface SendDocumentTableItem extends SendDocumentGetModel {
  /** 文書種類名 */
  categoryName: string;
  /** 返信済み人数 */
  replyedCount: number | null;
  /** 未返信人数 */
  nonreplyedCount: number | null;
}

export interface FilterSetting {
  categoryId: string;
  searchWord: string;
}
</script>

<style lang="scss" scoped>
.send-documents-table-component {
  .edit-btn {
    width: 120px;
  }
  .delete-btn {
    width: 120px;
  }
}
</style>
